import React, { useState, useCallback } from 'react';
import Layout from '../../components/layout';
import PageContainer from '../../components/PageContainer';
import { motion } from "framer-motion";
import { ArrowRight, Loader2, X } from "lucide-react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import impact from "./vc_report.png";
import image1 from "./1.png";
import minimalistImage from "./Minimalist.png";
import { baseUrl } from '../../../Config/config';
import { validateEmail, isRestrictedEmail, RESTRICTED_ACCESS_MESSAGE } from '../../utils/emailValidation';
import RestrictedAccessModal from '../../components/RestrictedAccessModal';
import VIAssistantPreview from '../../components/VIAssistantPreview';

const IndiaVcReportInner = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    name: '',
    firm_name: '',
    designation: '',
    email: '',
    city: '',
    country: '',
    phone: '',
    firm_type: '',
    receive_directory: '',
    about_our_service: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [restricted, setRestricted] = useState('');
  const [restrictedOpen, setRestrictedOpen] = useState(false);

  // The email field carries whichever message is currently blocking the submit,
  // so the disabled button always has a visible reason next to it - including
  // after the restricted-access modal has been dismissed.
  const emailFieldError = emailError || restricted;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'email') {
      if (emailError) setEmailError('');
      if (restricted) setRestricted('');
    }
  };

  const handleEmailBlur = () => {
    setEmailError(formData.email.trim() ? validateEmail(formData.email) : '');
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (restricted) return;

    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      setError('');
      return;
    }

    if (!executeRecaptcha) {
      setError('reCAPTCHA not ready, please try again.');
      return;
    }

    setLoading(true);
    setError('');

    let recaptchaToken;
    try {
      recaptchaToken = await executeRecaptcha('ivc_report');
    } catch {
      setLoading(false);
      setError('reCAPTCHA check failed. Please try again.');
      return;
    }

    try {
      const jsonData = {
        ...formData,
        recaptcha: recaptchaToken
      };

      const response = await fetch(`${baseUrl}/iVcReport`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jsonData),
      });

      // On success the server streams the report PDF itself; every other
      // outcome (errors, restricted email) is JSON.
      const isPdf = (response.headers.get('Content-Type') || '').includes('application/pdf');

      if (response.ok && isPdf) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "India_VC_Landscape_Report.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);

        setSuccess(true);
        setShowForm(false);
        return;
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) throw new Error(data.message || 'Failed to submit form');

      // The server records the submission either way and returns eligible:false
      // when the report must be withheld. isRestrictedEmail() is only a fallback
      // for a backend that predates the flag - safe to drop once it is live - so
      // it must not override an explicit eligible:true.
      if (data.eligible === false
        || (data.eligible === undefined && isRestrictedEmail(formData.email))) {
        setShowForm(false);
        setRestricted(data.message || RESTRICTED_ACCESS_MESSAGE);
        setRestrictedOpen(true);
        return;
      }

      throw new Error(data.message || 'Failed to download the report');
      
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [executeRecaptcha, formData, restricted]);

  const formFields = [
    { name: 'name', type: 'text', label: 'Your Name' },
    { name: 'firm_name', type: 'text', label: 'Firm Name' },
    { name: 'designation', type: 'text', label: 'Designation' },
    { name: 'email', type: 'email', label: 'Email Id' },
    { name: 'city', type: 'text', label: 'City' },
    { name: 'country', type: 'text', label: 'Country' },
    { name: 'phone', type: 'text', label: 'Phone' },
  ];

  const firmTypes = [
    { id: 'pevc-investor', label: 'PE/VC Investor' },
    { id: 'company-seeking-funding', label: 'Company Seeking Funding' },
    { id: 'advisory-firm', label: 'Advisory Firm' },
    { id: 'other', label: 'Other' }
  ];

  const directoryOptions = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' }
  ];

  return (
    <Layout>
      <PageContainer
        title="India Venture Capital Landscape Report - 2025 | Venture intelligence"
        description="Explore the India Venture Capital Landscape Report – trends, data, and deep insights into the VC ecosystem in 2025."
        additionalMetaTags={[
          { property: "og:type", content: "website" },
          { property: "og:url", content: "https://www.ventureintelligence.com/india-venture-capital-landscape-report-2025" },
          { property: "og:title", content: "India Venture Capital Report 2025 – Venture Intelligence" },
          { property: "og:description", content: "India Venture Capital Report 2025: Explore the India Venture Capital Landscape Report – trends, data, and deep insights into the VC ecosystem in 2025." },
          { name: "keywords", content: "Venture Capital India, Private Equity India, Venture Capital Landscape Report, VC Landscape Report, VC Funding India, PE Deals, Exit Valuations, Startup Investments India, Indian VC Reports, Deal Intelligence, Investment Reports India, Unicorns, IPO Exits, Buyouts India, VC Trends, Growth Capital India, Deal Flow Analysis, Investor Reports India, Market Insights, Indian Startup Ecosystem, VC Performance Metrics, Investment Research India, Deal Pipeline Reports, Fundraising Trends, Venture Intelligence Reports, Early Stage Investing, Portfolio Valuations India, Venture Capital Exit Strategies in India, Venture Capital, Private Equity, Deal Trends, Homegrown VC, Deep Tech Report, Valuations, India, Startup, Limited Partner, Startup Performance" },
          { name: "robots", content: "index, follow" },
          { name: "title", content: "India Venture Capital Landscape Report 2025 – Venture Intelligence" }
        ]}
      >
      <div className="min-h-screen">
        <div className="relative h-48 mb-12">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${impact})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
          </div>
        </div>

        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-5xl mx-auto mb-8"
          >
            <h2 className="text-2xl font-semibold text-gray-800 mb-8 text-center">
              Executive Summary
            </h2>

            {/* First section with minimalist image spanning two sections */}
            <div className="relative">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 ">
                Evolving Landscape
              </h3>
              <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                Ever since the hyper funding years of 2021-2022, the Venture Capital (VC) landscape in India has been witnessing very significant changes. An obvious positive change has been the emergence of the Domestic IPO market as a viable option for VC exits. Even as VC investors across the world have struggled to find liquidity for their investments, the Indian IPO market has stood out as a shining star, welcoming startups with open arms - in the process, providing attractive returns to their VC investors.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-4 ">
                Beyond IPOs
              </h3>
              {/* Beyond IPOs section with text on left and image on right */}
              <div className="flex flex-col xl:flex-row xl:items-start xl:gap-8 mb-6">
                <div className="xl:flex-1 xl:pr-4">
                  <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                    And, it is not just IPOs. On the same day in January 2025 - the 23rd to be specific - two strategic acquisitions of startups, totaling over $500 million in value were announced: that of VC-backed direct-to-consumer (D2C) personal care brand Minimalist (providing a 10x return to the first VC investor) and bootstrapped Software-as-a-Service (SaaS) product maker Wingify (which saw the founder cashing out over $140 million).
                  </p>
                  <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                    The long touted "India Growth Story" factors - from consistent economic growth to large & growing consuming class and demographic dividend; to the abundant English-speaking tech talent; to the digital revolution - are converging to deepen the market for startup offerings like never before. In turn, this is leading to real venture scale revenues and outcomes.
                  </p>
                  
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 ">
                    Rise of The Home Grown
                  </h3>
                  <p className="text-base text-gray-700 leading-relaxed text-justify">
                    Easier exits are not the only major change in the Indian VC landscape. Any slicing of the investor data since 2022 shows the rise of Homegrown fund managers. With the local units of various international VC firms re-branding/spinning-out/splitting-up from their parent firms, the list of top most active investor names in the Indian market looks radically different from just a couple of years ago.
                  </p>
                </div>
                <div className="xl:w-80 xl:h-[450px] mt-0 xl:-mt-8 flex-shrink-0">
                  <img 
                    src={minimalistImage} 
                    alt="Minimalist Brand" 
                    className="w-full h-full rounded-lg shadow-md object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Second section with first image spanning two sections */}
            <div className="relative">
              <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                Over a dozen homegrown firms have raised their third fund - with five of them breaching the coveted Fund IV, earning them the "Franchise Fund" tag. Clearly, this milestone has been reached on the back of actual returns from the early funds - something that is evidenced by the return data of 2011 to 2016 vintage funds.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mb-4 ">
                Domestic LPs To The Fore
              </h3>
              {/* Domestic LPs section with text on left and image on right */}
              <div className="flex flex-col xl:flex-row xl:items-start xl:gap-8 mb-6">
                <div className="xl:flex-1 xl:pr-4">
                  <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                    The Narendra Modi-led government was the first ever Central Government to identify startups as an area of thrust, announcing the "Startup India Initiative" in 2016. This has since percolated to various state (federal) level policy initiatives.
                  </p>
                  <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                    The launch of the Indian government sponsored SIDBI Fund of Funds (with an initial corpus of INR 10,000 crore) in 2016 has been a game changer.
                  </p>
                  <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                    While Family Offices - led by those of the founders of IT Services giants like Wipro and Infosys - have been investing both in startups and VC funds for over a decade now, the SIDBI FoF has laid to rest the question around why domestic money is not participating in the VC asset class. So much so that, today domestic capital accounts for half the corpuses of most new VC funds - across the stage and size spectrum.
                  </p>
                  <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
                    The Government of India announced another Fund of Funds in 2020 in the form of the Self Reliant India (SRI) Fund, with a corpus of INR 10,000 crore.
                  </p>
                </div>
                <div className="xl:w-80 xl:h-96 mt-0 xl:mt-0 flex-shrink-0">
                  <img 
                    src={image1} 
                    alt="Domestic LPs" 
                    className="w-full h-full rounded-lg shadow-md object-cover"
                  />
                </div>
              </div>
              
              {/* Remove the old absolute positioned image */}
            </div>

            <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
              The number of Family Offices in India is also on the rise. According to an EY–Julius Baer estimate, the country now hosts over 300 family offices, up from just 45 in 2018. From traditional business families to startup founders who have had good exits, they are actively investing in the VC-startup ecosystem, creating a virtuous cycle.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              About the Report
            </h3>
            <p className="text-base text-gray-700 mb-6 leading-relaxed text-justify">
              The Venture Intelligence Indian VC Landscape Report aims to be a one point source for readers to track and make sense of the various significant changes in the ecosystem. From highlighting key exit trends to the underlying driving factors such as public markets and the macro economy to VC fundraising and investment activity, the Report aims to serve as a comprehensive annual update for investors in the Indian VC asset class.<br />
            </p>

            <div className="flex justify-center mt-8">
              <button
                onClick={() => setShowForm(true)}
                className="px-8 py-3 bg-[#bc8b20] hover:bg-[#d6b15f] text-white font-semibold rounded-lg transition-colors duration-200 flex items-center"
              >
                Download the Report <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Form Popup */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Just complete the following details to download the report.
                </h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                    {formFields.map((field) => (
                      <div key={field.name} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 capitalize">
                          {field.label}*
                        </label>
                        <input
                          type={field.type}
                          name={field.name}
                          required
                          value={formData[field.name]}
                          onChange={handleChange}
                          onBlur={field.name === 'email' ? handleEmailBlur : undefined}
                          aria-invalid={field.name === 'email' && !!emailFieldError}
                          aria-describedby={field.name === 'email' && emailFieldError ? 'email-error' : undefined}
                          className={`block w-full rounded-md shadow-sm sm:text-sm px-4 py-2 border ${
                            field.name === 'email' && emailFieldError
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                              : 'border-gray-300 focus:border-[#c59c40] focus:ring-[#c59c40]'
                          }`}
                        />
                        {field.name === 'email' && emailFieldError && (
                          <p id="email-error" className="text-red-600 text-xs">{emailFieldError}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Firm Type*
                    </label>
                    <div className="flex flex-nowrap overflow-x-auto gap-4 pb-2">
                      {firmTypes.map((type) => (
                        <div key={type.id} className="flex-shrink-0">
                          <div className="flex items-center h-12 bg-gray-50 rounded-lg px-4 hover:bg-gray-100 transition-colors whitespace-nowrap">
                            <input
                              type="radio"
                              id={type.id}
                              name="firm_type"
                              value={type.id}
                              onChange={handleChange}
                              required
                              className="focus:ring-[#c59c40] h-4 w-4 text-[#c59c40] border-gray-300"
                            />
                            <label htmlFor={type.id} className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                              {type.label}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Would you also like to receive a Directory of Most Active VC Investors in India?*
                    </label>
                    <div className="flex gap-4">
                      {directoryOptions.map((option) => (
                        <div key={option.id} className="flex items-center h-12 bg-gray-50 rounded-lg px-4 hover:bg-gray-100 transition-colors">
                          <input
                            type="radio"
                            id={`directory-${option.id}`}
                            name="receive_directory"
                            value={option.id}
                            onChange={handleChange}
                            required
                            className="focus:ring-[#c59c40] h-4 w-4 text-[#c59c40] border-gray-300"
                          />
                          <label htmlFor={`directory-${option.id}`} className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div> */}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      How did you learn about our service?*
                    </label>
                    <textarea
                      name="about_our_service"
                      required
                      value={formData.about_our_service}
                      onChange={handleChange}
                      rows={3} 
                      className="block w-[75%] rounded-md border-gray-300 shadow-sm focus:border-[#c59c40] focus:ring-[#c59c40] sm:text-sm px-4 py-2 border"
                    />
                  </div>

                  {error && (
                    <p className="text-red-600 text-sm text-center">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !!emailFieldError}
                    className="w-full max-w-md mx-auto flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#bc8b20] hover:bg-[#d6b15f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c59c40] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Submit <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </button>

                </form>
              </div>
            </motion.div>
          </div>
        )}
        {/* VI Assistant promo - a styled preview of the assistant's welcome
            screen, shown once the thank-you modal has been dismissed. */}
        {!showForm && success && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="relative bg-white rounded-xl shadow-lg max-w-6xl w-full max-h-[95vh] overflow-hidden"
            >
              <button
                onClick={() => {setSuccess(false); setShowForm(false)}}
                aria-label="Close"
                className="absolute top-3 right-3 z-10 text-white/60 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>

              <VIAssistantPreview />
            </motion.div>
          </div>
        )}
        <RestrictedAccessModal
          open={restrictedOpen}
          onClose={() => setRestrictedOpen(false)}
          message={restricted}
        />
      </div>
      </PageContainer>
    </Layout>
  );
};

const IndiaVcReport = () => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY}>
    <IndiaVcReportInner />
  </GoogleReCaptchaProvider>
);

export default IndiaVcReport;